/**
 * Main Entry Point - Gleem Core Frontend
 */

import { createApp } from 'vue'
import { createPinia, setActivePinia } from 'pinia'
import App from './App.vue'
import router from './core/router'
import { useThemeStore } from './core/stores/theme'
import { useSettingsStore } from './core/stores/settings'
import { avviaMonitor } from './core/monitor/avvia'

// Styles
import './styles/main.css'

// Create app
const app = createApp(App)

// Monitor errori (Sickbay), col DSN preso A RUNTIME dal proprio backend su
// GET /api/public/config: niente VITE_SENTRY_DSN nel bundle, niente sentry.io
// (spento il 31/08). Non si aspetta: parte prima del mount e non lo blocca; se
// il backend risponde `null` o non risponde, non parte nulla e l'app e' identica.
// Dettagli e rumore filtrato: core/monitor/avvia.ts
void avviaMonitor(app)

// Pinia store
const pinia = createPinia()
app.use(pinia)
setActivePinia(pinia)

// Router
app.use(router)

// Initialize theme — synchronous, must be before mount to prevent flash
const theme = useThemeStore(pinia)
theme.init()

// Mount IMMEDIATELY — do not block on router.isReady() or data loading.
// Router guards handle auth-dependent navigations; data is loaded in parallel below.
app.mount('#app')

// Defer settings load to AFTER mount — no blocking on FCP.
// Note: user fetch is already triggered by auth store init when a token is present,
// and awaited by the router RBAC guard for /eb/* routes. No extra call needed here.
router.isReady().then(() => {
  const settingsStore = useSettingsStore(pinia)
  settingsStore.loadSettings().catch((err: unknown) => {
    console.error('Settings load failed:', err)
  })
})

console.log('🚀 Gleem Core Frontend initialized')

// ============================================================================
// Service Worker — feature-flagged via VITE_ENABLE_SW.
//
// Three layers of safety:
//  1. Build-time disable: vite-plugin-pwa is configured with `disable: true`
//     when VITE_ENABLE_SW !== 'true', so no SW code is generated at all.
//  2. Runtime kill-switch: before registering, fetch `/sw-kill.json`. If it
//     returns `{"kill": true}`, unregister any existing SW and abort. This
//     lets us disable all SWs in the wild without a redeploy.
//  3. Auto-update flow: on new version detected, we just log + expose an
//     event the UI can listen to (see SWUpdatePrompt component).
// ============================================================================
const SW_ENABLED = import.meta.env.VITE_ENABLE_SW === 'true'

if ('serviceWorker' in navigator) {
  if (SW_ENABLED) {
    // Dynamic import so the workbox-window bundle is only loaded when SW is on.
    ;(async () => {
      try {
        // Kill-switch check — 1s timeout, fail-open (if check fails, proceed).
        const killCheck = await fetch('/sw-kill.json', {
          cache: 'no-store',
          signal: AbortSignal.timeout(1000),
        })
          .then((r) => (r.ok ? r.json() : { kill: false }))
          .catch(() => ({ kill: false }))

        if (killCheck?.kill === true) {
          console.warn('[SW] Kill-switch active — unregistering all SWs')
          const regs = await navigator.serviceWorker.getRegistrations()
          await Promise.all(regs.map((r) => r.unregister()))
          return
        }

        const { registerSW } = await import('virtual:pwa-register')
        const updateSW = registerSW({
          immediate: true,
          onRegistered(reg) {
            console.log('[SW] Registered', reg?.scope)
            // Check più aggressivo per nuove versioni:
            //  - quando l'utente torna alla tab (visibilitychange)
            //  - ogni 5 minuti come fallback
            // Riduce la finestra in cui l'utente vede chunk 404 dopo un deploy.
            if (reg) {
              const triggerUpdate = () => {
                reg.update().catch(() => {/* silenziosi: il prossimo check riproverà */})
              }
              document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') triggerUpdate()
              })
              setInterval(triggerUpdate, 5 * 60 * 1000)
            }
          },
          onRegisterError(err) {
            console.error('[SW] Registration error', err)
          },
          onNeedRefresh() {
            console.log('[SW] New version available')
            // Expose an event the UI can react to (see SWUpdatePrompt.vue).
            window.dispatchEvent(
              new CustomEvent('gleem:sw-update-available', {
                detail: { update: () => updateSW(true) },
              })
            )
          },
          onOfflineReady() {
            console.log('[SW] Offline-ready')
          },
        })
      } catch (err) {
        console.error('[SW] Bootstrap failed', err)
      }
    })()
  } else {
    // SW disabled — aggressively unregister any stale SW from a previous build.
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => r.unregister())
      if (regs.length > 0) {
        console.log('[SW] Unregistered stale Service Workers:', regs.length)
      }
    })
  }
}

